import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogRef,
    MatDialogClose,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import { SnackbarService } from '@services/snackbar.service';
import { DiscrepancyService } from '@services/api/discrepancy/discrepancy.service';
import { LoaderComponent } from '@shared/loader/loader.component';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { DiscrepancyIdNo } from '@models/discrepancy/item';
import { refreshActionRelatedData } from 'app/store/actions/discrepancy.actions';
import DATA_QA from '@automation/data-qa.json';
import { DocumentService } from '@services/api/document/document.service';
import { DocumentRevisionVersion } from '@models/document';
import { DocumentDataService } from '@services/data/document-data.service';
import { RevisionDataService } from '@services/data/revision-data-service';

@Component({
    templateUrl: 'recall-for-edit-dialog.component.html',
    imports: [FlatButtonComponent, MatDialogClose, LoaderComponent],
})
export class RecallForEditDialogComponent implements OnDestroy, OnInit {
    loading = false;
    error = false;
    revisionsToBeDeleted: DocumentRevisionVersion[] = [];
    revisionsToBeSevered: DocumentRevisionVersion[] = [];
    readonly dataQa = DATA_QA;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;

    private readonly store = inject(Store);
    private readonly snackbarService = inject(SnackbarService);
    private readonly discrepancyService = inject(DiscrepancyService);
    private readonly documentService = inject(DocumentService);
    private readonly documentDataService = inject(DocumentDataService);
    private readonly revisionDataService = inject(RevisionDataService);
    readonly data: DiscrepancyIdNo = inject(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(
        MatDialogRef<RecallForEditDialogComponent>,
    );

    ngOnDestroy() {
        this._subscriptions.unsubscribeAllSafe();
    }

    ngOnInit() {
        this.loadDocumentVersions();
    }

    recall() {
        this.loading = true;
        this.error = false;

        this._subscriptions['recall-for-edit'] = this.discrepancyService
            .recallForEditDiscrepancy(this.data.id)
            .subscribe({
                next: (success) => {
                    this.loading = false;
                    if (success) {
                        this.snackbarService.success({
                            variant: 'success',
                            header: `Discrepancy ${this.data.discrepancyNo} Recalled to Initial Review state!`,
                            content:
                                'Now you can edit discrepancy metadata and notify station & fixer.',
                        });
                        this.store.dispatch(refreshActionRelatedData());
                        this.dialogRef.close();
                    } else {
                        this.error = true;
                    }
                },
            });
    }

    private loadDocumentVersions() {
        const currDiscIdx = this.revisionDataService.discrepancyNos!.findIndex(
            (d) => d === this.data.discrepancyNo,
        );
        const relatedCorrections = this.revisionDataService.corrections!.filter(
            (c) => c.discrepancyRelations[currDiscIdx] !== null,
        );

        this.revisionsToBeDeleted = relatedCorrections.filter(
            (r) => r.discrepancyRelations.filter((dr) => dr).length === 1,
        );
        this.revisionsToBeSevered = relatedCorrections.filter(
            (r) => r.discrepancyRelations.filter((dr) => dr).length > 1,
        );
    }
}
